'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { EntryDetailPanel, detailGridCols } from '../EntryDetail/EntryDetailPanel'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses } from '../ProcessedEntry/DomainEntry/processedTypes'
import { CollapsibleSection } from './CollapsibleSection'
import { AssignFieldsWidget } from './AssignFieldsWidget'
import { SourceWidget } from './SourceWidget'
import { EditInputWidget } from './EditInputWidget'
import { AcceptEntryWidget } from './AcceptEntryWidget'
import { ClassificationWidget } from './ClassificationWidget'
import { apiGet, apiPost, useCatalog } from './entriesApi'
import { buildScamDataFromEntry } from './CasosView'
import type { EntryRow, ScamData, UserEntryRevisionStatusType } from './entryTypes'

// Mirrors the backend's own replaceSixthAndNinth (backend/utils/db/
// postManagement.ts) — masking a call's phone number before it lands in
// processed_entries.sender is accept-time behavior, applied here so the
// user sees the masked value immediately rather than only after the next
// refetch; the backend re-derives it independently regardless.
function replaceSixthAndNinth(phone?: string | null) {
  if (!phone || phone.length < 9) return phone
  const chars = phone.split('')
  chars[5] = '*'
  chars[8] = '*'
  return chars.join('')
}

type LiveDetail = {
  raw_entry_id?: string | null
  entry_id?: string | null
  revision_status?: string | null
  // Top-level user_entries column (ingest-time source, e.g. "twitter") —
  // not the sources catalog/source_id used for tenant scoping. Same field
  // unified_entries_flat exposes as `source`.
  source?: string | null
  raw_entries?: Record<string, any>
  processed_entries?: Record<string, any> | null
  user_entry_pipeline_steps?: EntryRow['user_entry_pipeline_steps']
}

// Standalone "Raw Entry / Processed Entry Management" panel — accept/
// reject/assign-fields/edit-input/classification for one user_entry,
// presenting (and saving) against whichever of raw_entries/processed_entries
// currently represents it. Self-contained: only needs a userEntryId, fetches
// its own live state via GET /userentrydetail (same endpoint
// CaseEntryDetailPage's own refresh uses) rather than trusting a caller-
// supplied snapshot, so it drops into any entry-detail page (SibsMailView's
// SibsEntryDetailPage, CaseEntryDetailPage for Cases/Unified Entries, or a
// future one) without that caller needing to shape its own row into some
// particular adapter first.
//
// Presents as "Raw Entry Management" or "Processed Entry Management"
// depending on whether processed_entries actually exists for this
// user_entry (not source_type or any other caller-side guess) — every
// field-per-field COALESCE(processed, raw), same rule get_sibs_fraud_
// entries/unified_entries_flat apply everywhere else, so accepting an
// already-processed entry again (how a post-approval edit gets saved, see
// AcceptEntryWidget's own comment) never silently reverts an edit made
// since the original accept.
export function RawProcessedEntryPanel({
  userEntryId,
  theme,
  onUpdated,
  defaultOpen = false,
}: {
  userEntryId: string
  theme: ProcessedTheme
  /** Called after a successful accept/reject/field save/classification save, so the caller can refetch or patch its own list/detail state instead of it staying stale until a full reload. */
  onUpdated?: () => void
  /** Caller's own best guess at whether this entry still needs action (e.g. `!posted` off its own list/detail row) — the panel can't know this itself until its live fetch resolves, so it's not derived internally. */
  defaultOpen?: boolean
}) {
  const c = watchClasses(theme)
  const { catalog } = useCatalog()

  const [liveDetail, setLiveDetail] = useState<LiveDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [fieldOverrides, setFieldOverrides] = useState<Record<string, any>>({})
  const [revisionStatus, setRevisionStatus] = useState<string | undefined>(undefined)
  const [moderationLoading, setModerationLoading] = useState<UserEntryRevisionStatusType | undefined>()
  const [actionError, setActionError] = useState<string | undefined>()
  const [classificationSaving, setClassificationSaving] = useState(false)
  const [sourceSaving, setSourceSaving] = useState(false)

  const fetchLive = useCallback(async () => {
    const result = await apiGet<{ entry: LiveDetail | null }>(`/entry-detail?user_entry_id=${encodeURIComponent(userEntryId)}`)
    if (result.success && result.data?.entry) {
      setLiveDetail(result.data.entry)
      setRevisionStatus(result.data.entry.revision_status ?? undefined)
    }
    return result
  }, [userEntryId])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setLiveDetail(null)
    setFieldOverrides({})
    setActionError(undefined)
    fetchLive().finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [fetchLive])

  // True once a processed_entries row genuinely exists.
  const isProcessed = liveDetail ? liveDetail.entry_id != null : false
  const rawEntryId = liveDetail?.raw_entry_id ?? undefined

  // Per-field COALESCE(processed, raw) — the "human-reviewed value wins"
  // rule get_sibs_fraud_entries/unified_entries_flat both apply, kept
  // consistent here.
  const current = useMemo(() => {
    const liveRaw = liveDetail?.raw_entries
    const liveProcessed = liveDetail?.processed_entries
    const field = (key: string) => liveProcessed?.[key] ?? liveRaw?.[key] ?? null
    return {
      post_type: field('post_type'),
      sender: field('sender'),
      // Original detection time, not the accept/moderation time — always
      // off raw_entries specifically (processed_entries.created_at mirrors
      // this same value on insert; see acceptRawEntry), so accepting/
      // reclassifying an entry never moves its dashboard date-range bucket.
      created_at: liveRaw?.created_at ?? null,
      message_subject: field('message_subject'),
      message_content: field('message_content'),
      platform_id: field('platform_id'),
      platform_text: field('platform_text'),
      message_type_id: field('message_type_id'),
      call_type_id: field('call_type_id'),
      call_type_text: field('call_type_text'),
      is_scam: field('is_scam'),
      scam_claims: field('scam_claims'),
      scam_value: field('scam_value'),
    }
  }, [liveDetail])
  const editableRaw = { ...current, ...fieldOverrides }

  const entryRowForScam: EntryRow = useMemo(() => ({
    id: userEntryId,
    raw_entries: current,
    user_entry_pipeline_steps: liveDetail?.user_entry_pipeline_steps,
  }), [userEntryId, current, liveDetail?.user_entry_pipeline_steps])

  const [scamData, setScamData] = useState<ScamData>(() => buildScamDataFromEntry(entryRowForScam))
  const scamDataIsAutoRef = useRef(true)
  useEffect(() => { scamDataIsAutoRef.current = true }, [userEntryId])
  useEffect(() => {
    if (!scamDataIsAutoRef.current) return
    setScamData(buildScamDataFromEntry(entryRowForScam))
  }, [entryRowForScam])
  const setScamDataTracked: React.Dispatch<React.SetStateAction<ScamData>> = (value) => {
    scamDataIsAutoRef.current = false
    setScamData(value)
  }

  const posted = revisionStatus === 'accept' || revisionStatus === 'accept-hidden'

  // Mirrors AcceptEntryWidget's own disabled() — accept/accept-hidden stay
  // re-triggerable after the entry already carries that status (that's how
  // a post-approval edit gets saved), only reject/defer1 self-disable.
  const moderationDisabled = (status: UserEntryRevisionStatusType) =>
    Boolean(moderationLoading) || ((status === 'reject' || status === 'defer1') && revisionStatus === status)

  const handleAssignField = useCallback(async (field: 'platform' | 'messageType', id?: string, text?: string) => {
    const isCall = editableRaw.post_type === 'call'
    const delta: Record<string, any> = {}
    if (field === 'platform') {
      // Unlike message/call type, id and text always move together here: a
      // catalog pick sets both (id + its name), typing a manual value (no
      // catalog entry fits) sets text with id null, and clearing sends both
      // undefined — so, unlike the isCall branch below, text is never left
      // stale from a previous selection.
      delta.platform_id = id !== undefined && id !== '' ? Number(id) : null
      delta.platform_text = text ?? null
    } else if (isCall) {
      delta.call_type_id = id !== undefined && id !== '' ? Number(id) : null
      if (text !== undefined) delta.call_type_text = text ?? null
    } else {
      delta.message_type_id = id !== undefined && id !== '' ? Number(id) : null
    }
    setFieldOverrides((prev) => ({ ...prev, ...delta }))

    if (posted) {
      const payload: Record<string, any> = { userEntryId }
      if ('platform_id' in delta) payload.platformId = delta.platform_id
      if ('platform_text' in delta) payload.platformText = delta.platform_text
      if ('message_type_id' in delta) payload.messageTypeId = delta.message_type_id
      if ('call_type_id' in delta) payload.callTypeId = delta.call_type_id
      if ('call_type_text' in delta) payload.callTypeText = delta.call_type_text
      const result = await apiPost('/fields', payload)
      if (!result.success) setActionError(typeof result.error === 'string' ? result.error : 'Failed to save field')
      else onUpdated?.()
    }
  }, [editableRaw.post_type, posted, userEntryId, onUpdated])

  const handleEditField = useCallback((field: 'sender' | 'created_at' | 'message_subject' | 'message_content', value: string) => {
    setFieldOverrides((prev) => ({ ...prev, [field]: value }))
  }, [])

  const handlePlatformTextChange = useCallback((value: string) => {
    setFieldOverrides((prev) => ({ ...prev, platform_text: value || null }))
  }, [])

  // Saves straight away, unlike the fieldOverrides-then-Accept fields above:
  // source lives on user_entries directly, independent of whether the entry
  // has reached processed_entries yet, so there's no accept-time moment to
  // piggyback the write on.
  const handleSaveSource = useCallback(async (value: string) => {
    setSourceSaving(true)
    setActionError(undefined)
    const result = await apiPost('/fields', { userEntryId, source: value || null })
    if (result.success) {
      setLiveDetail((prev) => (prev ? { ...prev, source: value || null } : prev))
      onUpdated?.()
    } else {
      setActionError(typeof result.error === 'string' ? result.error : 'Failed to save source')
    }
    setSourceSaving(false)
  }, [userEntryId, onUpdated])

  const handleAccept = useCallback(async (accept: UserEntryRevisionStatusType) => {
    if (moderationDisabled(accept)) return
    if (!rawEntryId) return
    setModerationLoading(accept)
    setActionError(undefined)
    const raw = { ...current, ...fieldOverrides }
    const payload = {
      postType: raw.post_type,
      // Masking only applies going from raw -> processed for the first
      // time — once isProcessed, raw.sender already came from
      // processed_entries (see `current` above), which was already masked
      // (or never needed to be) by whichever accept created it. Re-masking
      // an already-masked number here would corrupt it further on every
      // re-accept of an already-processed call entry.
      from: raw.post_type === 'call' && !isProcessed ? replaceSixthAndNinth(raw.sender) : raw.sender,
      text: raw.message_content ?? null,
      subject: raw.message_subject ?? '',
      platformId: raw.platform_id,
      platformText: raw.platform_text ?? null,
      messageTypeId: raw.message_type_id ?? null,
      callTypeId: raw.call_type_id,
      callType: raw.call_type_text,
      isScam: scamData.isScam ?? null,
      scamClaims: scamData.scamClaims ?? null,
      scamValue: scamData.scamValue ?? null,
      rawEntryId,
      revisionStatus: accept,
      created_at: raw.created_at,
      updated_at: raw.created_at,
    }
    const result = await apiPost('/accept', payload)
    if (result.success) {
      setFieldOverrides({})
      setRevisionStatus(accept)
      // Re-fetch so the panel flips from "raw" to "processed" presentation
      // immediately, and any further edit on this same page reads/writes
      // through processed_entries rather than the now-stale raw snapshot.
      await fetchLive()
      onUpdated?.()
    } else {
      setActionError(typeof result.error === 'string' ? result.error : 'Failed to accept entry')
    }
    setModerationLoading(undefined)
  }, [current, fieldOverrides, scamData, rawEntryId, isProcessed, moderationLoading, revisionStatus, fetchLive, onUpdated])

  const handleReject = useCallback(async (status: UserEntryRevisionStatusType) => {
    if (moderationDisabled(status)) return
    if (!rawEntryId) return
    setModerationLoading(status)
    setActionError(undefined)
    const result = await apiPost('/reject', { rawEntryId, revisionStatus: status })
    if (result.success) {
      setRevisionStatus(status)
      onUpdated?.()
    } else {
      setActionError('Failed to reject entry')
    }
    setModerationLoading(undefined)
  }, [rawEntryId, moderationLoading, revisionStatus, onUpdated])

  const handleSaveClassification = useCallback(async () => {
    setClassificationSaving(true)
    setActionError(undefined)
    const existingManualCount = (liveDetail?.user_entry_pipeline_steps ?? []).filter((s) => s.step?.startsWith('classify_manual_update')).length
    const stepName = `classify_manual_update_${existingManualCount + 1}`
    const result = await apiPost('/classification', {
      userEntryId,
      step: stepName,
      isScam: scamData.isScam,
      scamClaims: scamData.scamClaims,
      scamValue: scamData.scamValue,
      translation: scamData.translation,
      hidden: scamData.hidden,
      hideClassification: scamData.hideClassification,
    })
    if (result.success) {
      await fetchLive()
      onUpdated?.()
    } else {
      setActionError('Failed to save classification')
    }
    setClassificationSaving(false)
  }, [liveDetail?.user_entry_pipeline_steps, userEntryId, scamData, fetchLive, onUpdated])

  if (loading) {
    return (
      <CollapsibleSection label="Entry Management" theme={theme} defaultOpen={defaultOpen}>
        <div className={`flex items-center gap-2 py-6 justify-center font-mono text-[11px] ${c.faint}`}>
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading entry…
        </div>
      </CollapsibleSection>
    )
  }

  return (
    <CollapsibleSection label={isProcessed ? "Processed Entry Management" : "Raw Entry Management"} theme={theme} defaultOpen={defaultOpen}>
      {/* grid-cols-1 below sm, matching EntryDetailShell's own responsive
          grid — EntryDetailPanel's cols prop only verticalizes into one
          column on mobile if its parent grid does too. */}
      <div className={`grid gap-3 ${detailGridCols(4)}`}>
        {actionError && <div className="col-span-1 font-mono text-xs text-destructive sm:col-span-4">{actionError}</div>}

        <EntryDetailPanel label="Accept / Reject" theme={theme} cols={4}>
          <AcceptEntryWidget
            theme={theme}
            currentRevisionStatus={revisionStatus}
            loadingAction={moderationLoading}
            onAccept={() => handleAccept('accept')}
            onAcceptHidden={() => handleAccept('accept-hidden')}
            onReject={() => handleReject('reject')}
            onDefer={() => handleReject('defer1')}
            isScam={scamData.isScam}
            onIsScamChange={(v) => setScamDataTracked({ ...scamData, isScam: v })}
          />
        </EntryDetailPanel>

        <EntryDetailPanel label="Assign Platform / Message Type" theme={theme} cols={2}>
          <div className="space-y-3">
            <AssignFieldsWidget
              theme={theme}
              catalog={catalog}
              postType={editableRaw.post_type}
              messageTypeId={editableRaw.post_type === 'call' ? editableRaw.call_type_id : editableRaw.message_type_id}
              platformId={editableRaw.platform_id}
              platformText={editableRaw.platform_text}
              onAssign={handleAssignField}
              onPlatformTextChange={handlePlatformTextChange}
            />
            <SourceWidget
              theme={theme}
              value={liveDetail?.source}
              onSave={handleSaveSource}
              saving={sourceSaving}
            />
          </div>
        </EntryDetailPanel>

        <EntryDetailPanel label="Edit Input" theme={theme} cols={2}>
          <EditInputWidget
            theme={theme}
            postType={editableRaw.post_type}
            sender={editableRaw.sender}
            createdAt={editableRaw.created_at}
            messageSubject={editableRaw.message_subject}
            messageContent={editableRaw.message_content}
            onChange={handleEditField}
            hint="Changes apply when you click Accept."
          />
        </EntryDetailPanel>

        <EntryDetailPanel label="Classification" theme={theme} cols={4} scrollable>
          <ClassificationWidget
            theme={theme}
            scamData={scamData}
            setScamData={setScamDataTracked}
            onSave={handleSaveClassification}
            saving={classificationSaving}
          />
        </EntryDetailPanel>
      </div>
    </CollapsibleSection>
  )
}
